import type { DashboardDecimal } from "./types";
import { formatExactCents } from "@/lib/currency";

export interface DashboardFormattedValue {
  full: string;
  compact?: string;
  accessibleLabel: string;
}

interface DecimalFormatOptions {
  trimTrailingZeros?: boolean;
}

const DECIMAL_PATTERN = /^([+-]?)(\d+)(?:\.(\d+))?$/;

const COMPACT_UNITS = [
  { divisor: 1_000_000_000_000_000n, suffix: "qua" },
  { divisor: 1_000_000_000_000n, suffix: "tri" },
  { divisor: 1_000_000_000n, suffix: "bi" },
  { divisor: 1_000_000n, suffix: "mi" },
  { divisor: 1_000n, suffix: "mil" },
] as const;

const normalizeWhole = (whole: string) => whole.replace(/^0+(?=\d)/, "");

const groupWhole = (whole: string) =>
  normalizeWhole(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const parseDecimal = (value: DashboardDecimal) => {
  const match = DECIMAL_PATTERN.exec(value);
  if (!match) throw new TypeError("dashboard decimal must be an exact string");

  return {
    sign: match[1] ?? "",
    whole: normalizeWhole(match[2] ?? "0"),
    fraction: match[3] ?? "",
  };
};

const formatCompactMagnitude = (
  magnitude: bigint,
  sign = "",
): string | undefined => {
  const unit = COMPACT_UNITS.find(({ divisor }) => magnitude >= divisor);
  if (!unit) return undefined;

  const tenths = (magnitude * 10n) / unit.divisor;
  const whole = tenths / 10n;
  const fraction = tenths % 10n;
  const decimal = fraction === 0n ? "" : `,${fraction}`;

  return `${sign}${groupWhole(whole.toString())}${decimal} ${unit.suffix}`;
};

const exactSafeInteger = (value: number, label: string): bigint => {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a nonnegative safe integer`);
  }
  return BigInt(value);
};

export function formatDashboardCurrency(
  cents: number,
): DashboardFormattedValue {
  const exactCents = exactSafeInteger(cents, "dashboard cents");
  const full = formatExactCents(exactCents);
  const compactMagnitude = formatCompactMagnitude(exactCents / 100n);

  return {
    full,
    ...(compactMagnitude ? { compact: `R$\u00a0${compactMagnitude}` } : {}),
    accessibleLabel: full,
  };
}

export function formatDashboardCount(value: number): DashboardFormattedValue {
  const exact = exactSafeInteger(value, "dashboard count");
  const full = groupWhole(exact.toString());
  const compact = formatCompactMagnitude(exact);

  return {
    full,
    ...(compact ? { compact } : {}),
    accessibleLabel: full,
  };
}

export function formatDashboardDecimal(
  value: DashboardDecimal,
  options: DecimalFormatOptions = {},
): DashboardFormattedValue {
  const parsed = parseDecimal(value);
  const fraction = options.trimTrailingZeros
    ? parsed.fraction.replace(/0+$/, "")
    : parsed.fraction;
  const full = `${parsed.sign}${groupWhole(parsed.whole)}${fraction ? `,${fraction}` : ""}`;
  const compact = formatCompactMagnitude(
    BigInt(parsed.whole),
    parsed.sign === "-" ? "-" : parsed.sign,
  );

  return {
    full,
    ...(compact ? { compact } : {}),
    accessibleLabel: full,
  };
}

export function formatDashboardUnitQuantity(
  unit: string,
  value: DashboardDecimal,
): DashboardFormattedValue | null {
  const parsed = parseDecimal(value);
  const normalizedFraction = parsed.fraction.replace(/0+$/, "");
  if (unit === "kg" && normalizedFraction.length > 3) {
    return null;
  }
  if (
    (unit === "cx" || unit === "bdj" || unit === "sc" || unit === "un") &&
    /[1-9]/.test(parsed.fraction)
  ) {
    return null;
  }
  return formatDashboardDecimal(value, { trimTrailingZeros: true });
}

export function formatDashboardPercent(
  value: DashboardDecimal,
): DashboardFormattedValue {
  const decimal = formatDashboardDecimal(value, { trimTrailingZeros: true });
  const full = `${decimal.full}%`;

  return { full, accessibleLabel: full };
}
